import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { format } from "date-fns";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import DateFormField from "components/date-form-field";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ModalForm from "components/modal-form";
import {
  PATIENT_PRODUCT_STATUS_CANCELED,
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_REPAIR_REQUESTED,
  PATIENT_PRODUCT_STATUS_RETURNED,
  PATIENT_PRODUCT_STATUS_SHIPPED_TO_MANUFACTURER
} from "constants/patient-products";
import {
  patientProductsApi,
  useUpdatePatientProductMutation
} from "services/patient-products";
import { useUpdatePatientProductsDeliverHearingAidsMutation } from "services/patients";
import { useGetProductQuery } from "services/products";
import { formatDateOnlyValue } from "utils/common";
import { ActivePatientProduct } from "utils/patient-products.util";

import { WarrantyHelperText } from "./warranty-helper-text.component";

interface MarkProductsAsDeliveredModalProps {
  patientId: number;
  isOpen: boolean;
  onCancel: () => void;
  product?: ActivePatientProduct;
  refetchPatient?: () => void;
}

export const MarkProductsAsDeliveredModal: React.FC<
  MarkProductsAsDeliveredModalProps
> = ({ onCancel, isOpen, product, patientId, refetchPatient }) => {
  const productId = product?.id;
  const dispatch = useAppDispatch();

  const manufacturer_warranty = product?.manufacturer_warranty;
  const lost_and_damage_warranty = product?.lost_and_damage_warranty;

  const { data } = useGetProductQuery(
    {
      id: Number(product?.inventory_product?.product.id),
      // cpt_codes not needed in this flow; pass `false` explicitly so the
      // RTK cache key aligns with other call sites and doesn't fragment.
      multipleCptCodesFeature: false
    },
    {
      skip: !product?.inventory_product?.product.id
    }
  );

  const defaultValues = useMemo(() => {
    const delivered_at = new Date();

    const manufacturer_warranty_expiration_date = manufacturer_warranty
      ? formatDateOnlyValue(manufacturer_warranty) || null
      : data?.specification?.warranty_months
        ? new Date(
            new Date().setMonth(
              new Date().getMonth() + data?.specification?.warranty_months
            )
          )
        : null;

    const loss_damage_warranty_expiration_date = lost_and_damage_warranty
      ? formatDateOnlyValue(lost_and_damage_warranty) || null
      : data?.specification?.loss_damage_warranty_months
        ? new Date(
            new Date().setMonth(
              new Date().getMonth() +
                data?.specification?.loss_damage_warranty_months
            )
          )
        : null;

    return {
      delivered_at,
      manufacturer_warranty_expiration_date,
      loss_damage_warranty_expiration_date
    };
  }, [data, manufacturer_warranty, lost_and_damage_warranty]);

  const formMethods = useForm<{
    delivered_at: Date;
    manufacturer_warranty_expiration_date: Date | string | null;
    loss_damage_warranty_expiration_date: Date | string | null;
  }>({ defaultValues });

  const { control, setValue, getValues, reset } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const [updatePatientProduct, { isLoading: productIsSubmitting }] =
    useUpdatePatientProductMutation();

  const [
    updatePatientProductsDeliverHearingAids,
    { isLoading: productsIsSubmitting }
  ] = useUpdatePatientProductsDeliverHearingAidsMutation();

  const handleDeliverHearingAids = useCallback(() => {
    updatePatientProductsDeliverHearingAids({
      patientId: Number(patientId),
      delivered_at: format(new Date(getValues("delivered_at")), "yyyy-MM-dd")
    }).then((response) => {
      if ("error" in response) {
        dispatch(
          setCustomMessage(<ErrorSnackbarContent response={response} />)
        );
        dispatch(setOpen(true));
      } else {
        dispatch(patientProductsApi.util.invalidateTags(["PatientProducts"]));
        dispatch(setCustomMessage("Active Hearing Aids delivered!"));
        dispatch(setOpen(true));
        onCancel();
      }
    });
  }, [
    patientId,
    updatePatientProductsDeliverHearingAids,
    dispatch,
    getValues,
    onCancel
  ]);

  const handleDeliverPatientProduct = useCallback(
    (id: number) => {
      const {
        manufacturer_warranty_expiration_date,
        loss_damage_warranty_expiration_date,
        delivered_at
      } = getValues();
      updatePatientProduct({
        id: Number(id),
        status: PATIENT_PRODUCT_STATUS_DELIVERED,
        delivered_at: format(new Date(delivered_at), "yyyy-MM-dd"),
        manufacturer_warranty_expiration_date:
          manufacturer_warranty_expiration_date
            ? format(
                new Date(manufacturer_warranty_expiration_date),
                "yyyy-MM-dd"
              )
            : null,
        loss_damage_warranty_expiration_date:
          loss_damage_warranty_expiration_date
            ? format(
                new Date(loss_damage_warranty_expiration_date),
                "yyyy-MM-dd"
              )
            : null
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          refetchPatient?.();
          dispatch(patientProductsApi.util.invalidateTags(["PatientProducts"]));
          dispatch(setCustomMessage("Hearing aid is delivered to patient"));
          dispatch(setOpen(true));
          onCancel();
        }
      });
    },

    [dispatch, refetchPatient, onCancel, updatePatientProduct, getValues]
  );

  const isSubmitting = productIsSubmitting || productsIsSubmitting;

  useEffect(() => {
    setValue("delivered_at", new Date());
  }, [setValue, isOpen]);

  const repairRequested = Boolean(
    product?.statuses?.find((item) =>
      [
        PATIENT_PRODUCT_STATUS_REPAIR_REQUESTED,
        PATIENT_PRODUCT_STATUS_SHIPPED_TO_MANUFACTURER,
        PATIENT_PRODUCT_STATUS_CANCELED,
        PATIENT_PRODUCT_STATUS_RETURNED
      ].includes(item.value)
    )
  );

  return (
    <FormProvider {...formMethods}>
      <ModalForm
        title={
          productId
            ? `Mark Delivered to Patient`
            : `Mark All Products Delivered`
        }
        hideBackdrop={false}
        isOpen={isOpen}
        onCancel={onCancel}
        onSubmit={() =>
          productId
            ? handleDeliverPatientProduct(productId)
            : handleDeliverHearingAids()
        }
        isSubmitting={isSubmitting}
        maxWidth="md"
        showCancelButton
        submitButtonLabel="Save"
        dialogActionsSx={{
          flexDirection: "row-reverse",
          justifyContent: "end",
          gap: 2
        }}
      >
        <Stack gap={2}>
          <Stack paddingX={0.5} flexDirection="row" alignItems="end" gap={2}>
            <DateFormField
              name="delivered_at"
              label="Delivered At"
              control={control}
              required
              onChange={(value) => {
                if (!repairRequested) {
                  const warrantyValue = value ? new Date(value) : value;
                  const lossDamageWarrantyValue = value
                    ? new Date(value)
                    : value;

                  const hasInitialWarrantyDate = Boolean(manufacturer_warranty);
                  const hasInitialLossDamageWarrantyDate = Boolean(
                    lost_and_damage_warranty
                  );

                  if (
                    !hasInitialWarrantyDate &&
                    data?.specification?.warranty_months
                  ) {
                    setValue(
                      "manufacturer_warranty_expiration_date",
                      warrantyValue
                        ? new Date(
                            warrantyValue.setMonth(
                              warrantyValue.getMonth() +
                                data?.specification?.warranty_months
                            )
                          )
                        : warrantyValue
                    );
                  }

                  if (
                    !hasInitialLossDamageWarrantyDate &&
                    data?.specification?.loss_damage_warranty_months
                  ) {
                    setValue(
                      "loss_damage_warranty_expiration_date",
                      lossDamageWarrantyValue
                        ? new Date(
                            lossDamageWarrantyValue.setMonth(
                              lossDamageWarrantyValue.getMonth() +
                                data?.specification?.loss_damage_warranty_months
                            )
                          )
                        : lossDamageWarrantyValue
                    );
                  }
                }
              }}
              sx={{}}
            />
          </Stack>
          {!repairRequested && (
            <Box
              sx={{
                borderRadius: 5,
                border: "2px solid #2e9e44",
                width: "max-content",
                ul: { paddingLeft: "40px", paddingRight: "20px" }
              }}
            >
              <WarrantyHelperText />
            </Box>
          )}
          {product && !repairRequested && (
            <>
              <DateFormField
                name="manufacturer_warranty_expiration_date"
                label="MFR Warranty Date"
                control={control}
                sx={{}}
              />
              <Box
                sx={{
                  borderRadius: 5,
                  border: "2px solid #2e9e44",
                  width: "max-content",
                  ul: { paddingLeft: "40px", paddingRight: "20px" }
                }}
              >
                <ul>
                  <li>
                    Current MFR warranty date:{" "}
                    {manufacturer_warranty
                      ? formatDateOnlyValue(
                          manufacturer_warranty,
                          "MMM dd, yyyy"
                        )
                      : "-"}
                  </li>

                  <li>
                    Current MFR warranty months:{" "}
                    {data?.specification?.warranty_months || "-"}
                  </li>
                </ul>
              </Box>

              <DateFormField
                name="loss_damage_warranty_expiration_date"
                label="L&D Warranty Date"
                control={control}
                sx={{}}
              />

              <Box
                sx={{
                  borderRadius: 5,
                  border: "2px solid #2e9e44",
                  width: "max-content",
                  ul: { paddingLeft: "40px", paddingRight: "20px" }
                }}
              >
                <ul>
                  <li>
                    Current L&D warranty date{" "}
                    {lost_and_damage_warranty
                      ? formatDateOnlyValue(
                          lost_and_damage_warranty,
                          "MMM dd, yyyy"
                        )
                      : "-"}
                  </li>

                  <li>
                    Current L&D warranty months:{" "}
                    {data?.specification?.loss_damage_warranty_months || "-"}
                  </li>
                </ul>
              </Box>
            </>
          )}
        </Stack>
      </ModalForm>
    </FormProvider>
  );
};
